import React, { useState, useEffect, useRef } from 'react';
import { User, Send } from 'lucide-react';
import { getApiUrl, getConfig } from '../config';
import './UserReportForm.css';

const UserReportForm = ({ onReportGenerated, initialUsername = '', initialYear = '', onUrlUpdate }) => {
  const config = getConfig();
  const [username, setUsername] = useState(initialUsername);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [emojiIndex, setEmojiIndex] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [selectedYear, setSelectedYear] = useState(initialYear || null);
  const pollingIntervalRef = useRef(null);
  const yearRefs = useRef({});
  const autoSubmittedRef = useRef(false);
  
  const loadingEmojis = ['🔍', '📊', '🤖', '⚙️', '📝', '✨'];

  useEffect(() => {
    let interval;
    if (loading) {
      interval = setInterval(() => {
        setEmojiIndex((prev) => (prev + 1) % loadingEmojis.length);
      }, 500);
    }
    return () => {
      if (interval) clearInterval(interval);
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [loading, loadingEmojis.length]);

  useEffect(() => {
    if (reportData && typeof reportData.report === 'object' && Object.keys(reportData.report).length > 0) {
      const urlParams = new URLSearchParams(window.location.search);
      const yearFromUrl = urlParams.get('year');
      
      if (yearFromUrl && reportData.report[yearFromUrl]) {
        if (selectedYear !== yearFromUrl) {
          setSelectedYear(yearFromUrl);
          setTimeout(() => {
            const element = document.getElementById(`year-${yearFromUrl}`);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 100);
        }
      } else if (!selectedYear) {
        const firstYear = Object.keys(reportData.report).sort((a, b) => parseInt(b) - parseInt(a))[0];
        if (firstYear) {
          setSelectedYear(firstYear);
          if (onUrlUpdate) {
            onUrlUpdate({ year: firstYear });
          }
        }
      }
    }
  }, [reportData]);

  const checkReportStatus = async (username) => {
    try {
      const statusUrl = getApiUrl(config.api.endpoints.userReportStatus) + `?username=${encodeURIComponent(username)}`;
      const response = await fetch(statusUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (err) {
      console.error('Error checking report status:', err);
      throw err;
    }
  };

  const updateReportDataFromReports = (reports, usernameToUse) => {
    const reportMap = {};
    const commentsMap = {};
    let totalComments = 0;

    reports.forEach(r => {
      if (r.status === 'completed') {
        if (r.report) {
          reportMap[r.year] = r.report;
        }
        if (r.comments) {
          commentsMap[r.year] = r.comments;
          totalComments += r.comments_count || 0;
        }
      }
    });

    const result = {
      username: usernameToUse.trim(),
      report: reportMap,
      comments: commentsMap,
      comments_count: totalComments,
    };

    setReportData(result);
    
    if (onReportGenerated && Object.keys(reportMap).length > 0) {
      onReportGenerated(result);
    }

    return result;
  };

  const startPolling = (username) => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    const poll = async () => {
      try {
        const statusData = await checkReportStatus(username);
        
        if (statusData.success && statusData.data && statusData.data.reports) {
          const reports = statusData.data.reports;
          
          const completedReports = reports.filter(r => r.status === 'completed');
          const processingReports = reports.filter(r => r.status === 'processing');
          const failedReports = reports.filter(r => r.status === 'failed');
          
          if (completedReports.length > 0) {
            updateReportDataFromReports(reports, username);
          }
          
          if (processingReports.length === 0) {
            if (reports.length > 0) {
              setStatusMessage(completedReports.length > 0 ? 'Все отчеты готовы!' : 'Обработка завершена');
              setLoading(false);
              
              if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
              }
              
              if (failedReports.length > 0 && completedReports.length === 0) {
                setError('Ошибка при генерации отчета. Попробуйте еще раз.');
              }
            } else {
              setStatusMessage('Ожидание начала обработки...');
            }
          } else {
            const processingCount = processingReports.length;
            const completedCount = completedReports.length;
            if (completedCount > 0) {
              setStatusMessage(`Готово: ${completedCount} из ${reports.length} ${reports.length === 1 ? 'отчета' : 'отчетов'}. Обрабатывается: ${processingCount} ${processingCount === 1 ? 'год' : 'лет'}.`);
            } else {
              setStatusMessage(`Обработка... Генерируются отчеты за ${processingCount} ${processingCount === 1 ? 'год' : 'лет'}. Пожалуйста, подождите.`);
            }
          }
        }
      } catch (err) {
        console.error('Error during polling:', err);
        setStatusMessage('Проверка статуса...');
      }
    };

    poll();
    pollingIntervalRef.current = setInterval(poll, 3000);
  };

  const generateReport = async (usernameToUse) => {
    if (!usernameToUse.trim()) {
      setError('Пожалуйста, введите имя пользователя');
      return;
    }

    setLoading(true);
    setError(null);
    setReportData(null);
    setEmojiIndex(0);
    setStatusMessage('Создание задачи...');

    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    try {
      const apiUrl = getApiUrl(config.api.endpoints.userReport);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username: usernameToUse.trim() }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        if (data.data && data.data.reports) {
          const reports = data.data.reports;
          
          const completedReports = reports.filter(r => r.status === 'completed');
          const processingReports = reports.filter(r => r.status === 'processing');
          
          if (completedReports.length > 0) {
            const reportMap = {};
            const commentsMap = {};
            let totalComments = 0;

            completedReports.forEach(r => {
              if (r.report) {
                reportMap[r.year] = r.report;
              }
              if (r.comments) {
                commentsMap[r.year] = r.comments;
                totalComments += r.comments_count || 0;
              }
            });

            const result = {
              username: usernameToUse.trim(),
              report: reportMap,
              comments: commentsMap,
              comments_count: totalComments,
            };

            setReportData(result);
            
            if (onUrlUpdate) {
              onUrlUpdate({ username: usernameToUse.trim() });
            }
            
            if (onReportGenerated) {
              onReportGenerated(result);
            }
          }
          
          if (processingReports.length > 0) {
            if (completedReports.length > 0) {
              setStatusMessage(`Загружено готовых отчетов: ${completedReports.length}. Обрабатывается: ${processingReports.length} ${processingReports.length === 1 ? 'год' : 'лет'}.`);
            } else {
              setStatusMessage('Задача создана. Ожидание обработки...');
            }
            startPolling(usernameToUse.trim());
          } else if (completedReports.length > 0) {
            setLoading(false);
            setStatusMessage('Все отчеты готовы!');
          } else {
            setStatusMessage('Задача создана. Ожидание обработки...');
            startPolling(usernameToUse.trim());
          }
        } else {
          setStatusMessage('Задача создана. Ожидание обработки...');
          startPolling(usernameToUse.trim());
        }
      } else {
        throw new Error(data.error || 'Ошибка при создании задачи');
      }
    } catch (err) {
      setError(err.message || 'Произошла ошибка при создании задачи');
      setLoading(false);
      setStatusMessage('');
      
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (onUrlUpdate && username.trim()) {
      onUrlUpdate({ username: username.trim() });
    }
    generateReport(username);
  };

  useEffect(() => {
    if (initialUsername && initialUsername.trim()) {
      setUsername(initialUsername);
      if (!autoSubmittedRef.current) {
        autoSubmittedRef.current = true;
        if (onUrlUpdate) {
          onUrlUpdate({ username: initialUsername.trim() });
        }
        generateReport(initialUsername);
      }
    }
  }, [initialUsername]);

  return (
    <div className="user-report-container">
      <form onSubmit={handleSubmit} className="user-report-form">
        <div className="form-header">
          <h2>
            <User size={24} />
            Досье пользователя
          </h2>
        </div>

        <div className="form-group">
          <label htmlFor="username">Имя пользователя</label>
          <input
            type="text"
            id="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onBlur={(e) => {
              if (onUrlUpdate && e.target.value.trim()) {
                onUrlUpdate({ username: e.target.value.trim() });
              }
            }}
            placeholder="Введите имя пользователя"
            disabled={loading}
            required
          />
        </div>

        <div className="form-info">
          <small>
            ⏱️ Генерация отчета может занять 3-5 минут. Пожалуйста, подождите.
          </small>
        </div>

        {error && (
          <div className="error-message">
            <strong>Ошибка:</strong> {error}
          </div>
        )}

        {loading && (
          <div className="loading-container">
            <div className="spinner-emoji">{loadingEmojis[emojiIndex]}</div>
            <p className="loading-text">Генерация отчета...</p>
            {statusMessage && (
              <p className="loading-subtext">{statusMessage}</p>
            )}
            <p className="loading-subtext">Это может занять 3-5 минут. Пожалуйста, подождите...</p>
          </div>
        )}

        <button
          type="submit"
          className="submit-button"
          disabled={loading || !username.trim()}
        >
          <Send size={18} />
          Сформировать отчет
        </button>
      </form>

      {reportData && Object.keys(reportData.report).length > 0 && (
        <div className="report-result">
          <h3>Отчет для пользователя: {reportData.username}</h3>
          {loading && (
            <div className="report-info">
              <p>Некоторые отчеты еще обрабатываются. Показаны готовые отчеты:</p>
            </div>
          )}
          
          {typeof reportData.report === 'object' && Object.keys(reportData.report).length > 1 && (
            <div className="year-tabs">
              {Object.keys(reportData.report)
                .sort((a, b) => parseInt(b) - parseInt(a))
                .map((year) => (
                  <button
                    key={year}
                    type="button"
                    className={`year-tab ${selectedYear === year ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedYear(year);
                      if (onUrlUpdate) {
                        onUrlUpdate({ year: year });
                      }
                      const element = document.getElementById(`year-${year}`);
                      if (element) {
                        setTimeout(() => {
                          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }, 100);
                      }
                    }}
                  >
                    {year}
                  </button>
                ))}
            </div>
          )}
          
          <div className="report-content">
            {typeof reportData.report === 'string' ? (
              <div className="report-text">{reportData.report}</div>
            ) : (
              Object.keys(reportData.report).sort((a, b) => parseInt(b) - parseInt(a)).map((year) => (
                <div 
                  key={year} 
                  id={`year-${year}`}
                  className="report-year-section"
                  ref={(el) => {
                    if (el) {
                      yearRefs.current[year] = el;
                    }
                  }}
                >
                  <h4>{year} год</h4>
                  <div className="report-text">{reportData.report[year]}</div>

                  {reportData.comments && reportData.comments[year] && (
                    <div className="comments-proof">
                      <h5>Исходные комментарии ({reportData.comments[year].length})</h5>
                      <div className="comments-list">
                        {reportData.comments[year].map((comment, idx) => (
                          <div key={idx} className="comment-item">
                            <div className="comment-header">
                              <span className="comment-date">{comment.datetime}</span>
                              {comment.post_link && (
                                <a
                                  href={comment.post_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="comment-link"
                                >
                                  Открыть пост
                                </a>
                              )}
                            </div>
                            <div className="comment-content">{comment.content}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
          {reportData.comments_count !== undefined && (
            <div className="report-stats">
              Всего комментариев проанализировано: {reportData.comments_count}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserReportForm;
